/** Pure shaping for the admin cost console. No I/O, no React — unit tested. */
import {
  type ClusterStatus,
  type ServiceAllocation,
  sortByEnvironment,
} from './lib';

export type BucketRow = { subscription: string; bucket: string; cost: number };
export type ServiceRow = {
  service: string;
  env: string;
  subscription: string;
  resourceType: string;
  cost: number;
};
export type UnownedRow = {
  bucket: 'shared' | 'unallocated';
  resourceGroup: string;
  env: string;
  resourceType: string;
  cost: number;
};
export type Summary = {
  window: { start: string | null; end: string | null };
  buckets: BucketRow[];
  services: ServiceRow[];
  unowned: UnownedRow[];
};

export type Filters = { env: string; resourceType: string; service: string };
export const NO_FILTER = 'all';

/**
 * Azure attributes by resource group or `service` tag, OpenCost by the
 * backstage.io/kubernetes-id pod label. Same key, but the two sources need not
 * agree on its casing, so every comparison — filter, join and picker — folds
 * case through this one helper.
 */
export const serviceKey = (service: string) => service.trim().toLowerCase();

export const isNarrowed = (f: Filters): boolean =>
  f.env !== NO_FILTER || f.resourceType !== NO_FILTER || f.service !== NO_FILTER;

export const BUCKET_ORDER = ['cluster', 'service', 'shared', 'unallocated'] as const;
export type Bucket = (typeof BUCKET_ORDER)[number];

/** Totals per bucket across every subscription, in the order the bar renders. */
export function bucketTotals(rows: BucketRow[]): { bucket: Bucket; cost: number }[] {
  const by = new Map<string, number>();
  rows.forEach(r => by.set(r.bucket, (by.get(r.bucket) ?? 0) + r.cost));
  return BUCKET_ORDER.map(b => ({ bucket: b, cost: by.get(b) ?? 0 }));
}

/** Everything Azure billed, whatever the current filter is. */
export function estateTotal(buckets: BucketRow[]): number {
  return buckets.reduce((a, b) => a + b.cost, 0);
}

export function applyFilters(rows: ServiceRow[], f: Filters): ServiceRow[] {
  return rows.filter(
    r =>
      (f.env === NO_FILTER || r.env === f.env) &&
      (f.resourceType === NO_FILTER || r.resourceType === f.resourceType) &&
      (f.service === NO_FILTER || serviceKey(r.service) === serviceKey(f.service)),
  );
}

/**
 * Shared and unallocated rows carry no service label — the spend is precisely
 * the spend that resolved to no single service — so narrowing to one service
 * empties them rather than matching everything.
 */
export function applyUnownedFilters(rows: UnownedRow[], f: Filters): UnownedRow[] {
  if (f.service !== NO_FILTER) return [];
  return rows.filter(
    r =>
      (f.env === NO_FILTER || r.env === f.env) &&
      (f.resourceType === NO_FILTER || r.resourceType === f.resourceType),
  );
}

/**
 * Every environment present anywhere in the data. Service rows and unowned
 * rows do not cover the same set — platform resource groups sit in mgmt and
 * preprod, where no service does, and unallocated rows carry `unknown` — so
 * listing only the service environments makes that spend unreachable.
 */
export function envOptions(services: ServiceRow[], unowned: UnownedRow[]): string[] {
  return [...new Set([...services.map(s => s.env), ...unowned.map(u => u.env)])].sort();
}

/**
 * Bucket totals for the current filter. Cluster cost is reported per
 * subscription only — it carries no service, environment or resource-type
 * label — so it can answer the unfiltered question and nothing narrower, and
 * is omitted rather than repeated unchanged beside figures that did narrow.
 */
export function filteredBucketTotals(
  services: ServiceRow[],
  unowned: UnownedRow[],
  clusterTotal: number,
  narrowed: boolean,
): { bucket: Bucket; cost: number }[] {
  const by = new Map<string, number>();
  services.forEach(r => by.set('service', (by.get('service') ?? 0) + r.cost));
  unowned.forEach(r => by.set(r.bucket, (by.get(r.bucket) ?? 0) + r.cost));
  if (!narrowed) by.set('cluster', clusterTotal);
  return BUCKET_ORDER.filter(b => !(narrowed && b === 'cluster')).map(b => ({
    bucket: b,
    cost: by.get(b) ?? 0,
  }));
}

export type ServiceGroup = {
  service: string;
  cost: number;
  envs: string[];
  lines: ServiceRow[];
};

export function groupByService(rows: ServiceRow[]): ServiceGroup[] {
  const by = new Map<string, ServiceGroup>();
  rows.forEach(r => {
    const g =
      by.get(r.service) ?? { service: r.service, cost: 0, envs: [], lines: [] };
    g.cost += r.cost;
    if (!g.envs.includes(r.env)) g.envs.push(r.env);
    g.lines.push(r);
    by.set(r.service, g);
  });
  return [...by.values()].map(g => ({
    ...g,
    envs: g.envs.sort(),
    lines: g.lines.slice().sort((a, b) => b.cost - a.cost),
  }));
}

export function totalsByResourceType(rows: ServiceRow[]): { type: string; cost: number }[] {
  const by = new Map<string, number>();
  rows.forEach(r => by.set(r.resourceType, (by.get(r.resourceType) ?? 0) + r.cost));
  return [...by.entries()]
    .map(([type, cost]) => ({ type, cost }))
    .sort((a, b) => b.cost - a.cost);
}

export type KubernetesServiceRow = {
  service: string;
  environment: string;
  cost: number;
  /** Absent on payloads from before the breakdown was added. */
  allocation?: ServiceAllocation;
};

export type KubernetesSummary = {
  window: string;
  total: number;
  services: KubernetesServiceRow[];
  clusters: ClusterStatus[];
};

/**
 * The Azure resourceType filter has no Kubernetes equivalent, so it is ignored
 * here. The caller decides what that means for a merged view.
 */
export function applyKubernetesFilters(
  rows: KubernetesServiceRow[],
  f: Filters,
): KubernetesServiceRow[] {
  return rows.filter(
    r =>
      (f.env === NO_FILTER || r.environment === f.env) &&
      (f.service === NO_FILTER || serviceKey(r.service) === serviceKey(f.service)),
  );
}

export const kubernetesTotal = (rows: KubernetesServiceRow[]): number =>
  rows.reduce((sum, r) => sum + r.cost, 0);

export type MergedServiceRow = {
  /** Azure's spelling where Azure saw the service, otherwise Kubernetes's. */
  service: string;
  cost: number;
  azure: number;
  kubernetes: number;
  envs: string[];
  /** Empty means the source saw nothing — render an em dash, not $0.00. */
  azureLines: ServiceRow[];
  kubernetesLines: KubernetesServiceRow[];
};

/**
 * One row per service across both sources, joined on the case-folded service
 * name. Environments are a union: Azure derives one from the resource-group
 * prefix, OpenCost from the namespace, so a (service, environment) pair is
 * best-effort where the service name is not.
 *
 * Services with no Azure rows are the common case rather than an edge case —
 * most self-service Azure resources land in the shared cluster resource groups
 * and classify as Cluster cost before any tag is read.
 *
 * The two costs share a currency and very nearly a window: Azure covers seven
 * complete days ending a day or two back, OpenCost a trailing 7d to now.
 */
export function mergeServiceCosts(
  groups: ServiceGroup[],
  kubernetes: KubernetesServiceRow[],
): MergedServiceRow[] {
  const rows = new Map<string, MergedServiceRow>();

  for (const g of groups) {
    rows.set(serviceKey(g.service), {
      service: g.service,
      cost: g.cost,
      azure: g.cost,
      kubernetes: 0,
      envs: [...g.envs],
      azureLines: g.lines,
      kubernetesLines: [],
    });
  }

  for (const k of kubernetes) {
    const existing = rows.get(serviceKey(k.service));
    const row = existing ?? {
      service: k.service,
      cost: 0,
      azure: 0,
      kubernetes: 0,
      envs: [],
      azureLines: [],
      kubernetesLines: [],
    };
    row.cost += k.cost;
    row.kubernetes += k.cost;
    row.kubernetesLines = [...row.kubernetesLines, k];
    if (!row.envs.includes(k.environment)) {
      row.envs = [...row.envs, k.environment];
    }
    rows.set(serviceKey(k.service), row);
  }

  return [...rows.values()].map(row => ({
    ...row,
    // sortByEnvironment mutates, so it only ever sees a fresh array.
    envs: sortByEnvironment(row.envs.map(environment => ({ environment }))).map(
      e => e.environment,
    ),
    kubernetesLines: [...row.kubernetesLines].sort((a, b) => b.cost - a.cost),
  }));
}

export type SortKey = 'name' | 'cost' | 'azure' | 'kubernetes';

export function sortMergedRows(
  rows: MergedServiceRow[],
  sortKey: SortKey,
  dir: 1 | -1,
): MergedServiceRow[] {
  return rows
    .slice()
    .sort((a, b) =>
      sortKey === 'name'
        ? a.service.localeCompare(b.service) * dir
        : (a[sortKey] - b[sortKey]) * dir,
    );
}

/** The panel header states its own arithmetic, so the arithmetic is tested. */
export function mergedTotals(rows: MergedServiceRow[]): {
  azure: number;
  kubernetes: number;
  total: number;
} {
  return rows.reduce(
    (acc, r) => ({
      azure: acc.azure + r.azure,
      kubernetes: acc.kubernetes + r.kubernetes,
      total: acc.total + r.cost,
    }),
    { azure: 0, kubernetes: 0, total: 0 },
  );
}

export const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export const percent = (n: number) =>
  `${(n * 100).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
